
import type { IAPIJobPreviewForUser } from '@hokify/common';
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import { piniaAvailable } from '../helpers/piniaStoreAvailable';
import { fromNow } from '../helpers/datehelpers/from-now';
import { findLongestWord, shortenString } from '../helpers/stringhelper';
import { getAlternateUrls } from '../helpers/metadata';

export default defineComponent({
	name: 'JobListItemExperimental',
	data() {
		return { piniaAvailable };
	},
	computed: {
		topLevelDomain(): string | undefined {
			return piniaAvailable(this.$nuxt, '$userRootStore')
				? this.$nuxt.context.$userRootStore?.topLevelDomain
				: this.$store?.state?.topLevelDomain;
		},
		host(): string | undefined {
			return piniaAvailable(this.$nuxt, '$userRootStore')
				? this.$nuxt.context.$userRootStore?.host
				: this.$store?.state?.host;
		},
		jobTypes(): string[] | undefined {
			return this.job?.types?.slice(0, 2);
		}
	},
	methods: {
		shortenString,
		fromNow,
		nameContainsLongWord(name: string, limit = 35): boolean {
			const longestWord = findLongestWord(name);
			return longestWord.length > limit;
		},
		companyLink(job: IAPIJobPreviewForUser, isUserApp: boolean): string {
			let url = `/c/${job.company.alias}`;
			if (isUserApp) {
				url = `/pwa/c/${job.company._id}`;
			} else if (job.internal.region && job.internal.region !== this.topLevelDomain && this.host) {
				return getAlternateUrls(this.host, [job.internal.region])[0].href + url;
			}
			return url;
		},
		jobLink(job: IAPIJobPreviewForUser, isUserApp: boolean): string {
			let url = `/job/${job.jobNr}`;
			if (isUserApp) {
				url = `/pwa/job/${job.jobNr}`;
			} else if (job.internal.region && job.internal.region !== this.topLevelDomain && this.host) {
				return getAlternateUrls(this.host, [job.internal.region])[0].href + url;
			}
			return url;
		},
		cityLink(job: IAPIJobPreviewForUser): string {
			return `/jobs/search?city=${job.location.city}`;
		},
		jobTypeLink(type: string): string {
			return `/jobs/search?filters=jobtype-${type}`;
		},
		hasRequiredFields(job: IAPIJobPreviewForUser): boolean {
			let reqAv = false;
			if (job && job.requiredFields) {
				job.requiredFields.forEach(rqf => {
					if ((rqf as any).type === 'EDUCATIONLEVEL' || (rqf as any).type === 'JOBLEVEL') {
						reqAv = true;
					}
				});
			}
			return reqAv;
		},
		mapTag(tag: string): string {
			switch (tag) {
				// case 'finalcountdown':
				// 	return 'In Kürze offline';
				case 'newjob':
					return 'Neuer Job';
				case 'popularjob':
					return 'Beliebter Job';
				case 'nocv':
					return 'Ohne Lebenslauf bewerben';
				// case 'nopic':
				// 	return 'Bewerbung ohne Foto';
				case 'nomotivation':
					return 'Keine Motivationsabfrage';
				case 'nodocument':
					return 'Einfache Bewerbung';
				// case 'nobirthday':
				// 	return 'Keine Altersabfrage';
				// case 'nosalary':
				// 	return 'Keine Gehaltsabfrage';
				// case 'nodistancehome':
				// 	return 'Keine Wohnort Abfrage';

				// TODO: Falls Featureset socialmedia || socialmediapremium -> TAG: "Dringend Gesucht"
				default:
					return '';
			}
		},
		showDate(styling: string, job: IAPIJobPreviewForUser): Date | undefined | false {
			return styling !== 'offline' && job.internal && job.internal.publishedDate;
		},
		preventJobRedirect() {
			// prevent redirect to jobpage when a link is clicked
			this.$emit('prevent-job-redirect');
		},
		openInNewTab(url: string) {
			if (!this.isUserApp) {
				window.open(url, '_blank');
			}
		},
		unseenJob(lastSeen: Date, job: IAPIJobPreviewForUser): boolean {
			if (lastSeen && job?.internal?.publishedDate) {
				const date1 = new Date(lastSeen);
				const date2 = new Date(job.internal.publishedDate);
				return date1 < date2;
			}
			return false;
		},
		openInvisibleLink(link) {
			window.location.href = link;
		}
	},
	props: {
		isUserApp: { type: Boolean, default: true },
		attributes: { type: Object, required: false, default: () => null },
		job: { type: Object as PropType<IAPIJobPreviewForUser>, required: true },
		debug: { type: Object, required: false, default: () => null },
		enableDebug: { type: Boolean, default: false },
		jobIsSaved: { type: Boolean, default: false },
		styling: {
			type: String,
			default: 'live',
			validator: (value: string) => ['live', 'offline'].includes(value)
		},
		tagName: { type: String, default: '' },
		showTags: { type: Boolean, default: false },
		showDescription: { type: Boolean, default: true },
		minify: { type: Boolean, default: false },
		leanList: { type: Boolean, default: false },
		locationOnly: { type: Boolean, default: false },
		lastViewTime: { type: Date, default: null }
	}
});
